import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, Car, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog "Lier en masse" — scanne les engagements candidats (leasing/assurance auto non liés)
 * et propose de les lier au Parc Auto en une seule opération.
 */
export default function BulkLinkVehiclesDialog({ open, onOpenChange, token, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResults(null);
    setSelectedIds(new Set());
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/engagements/vehicle-candidates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCandidates(res.data.candidates || []);
        // Pré-sélectionne les candidats avec confidence élevée
        const preSelected = new Set(
          (res.data.candidates || [])
            .filter(c => c.confidence >= 30)
            .map(c => c.id)
        );
        setSelectedIds(preSelected);
      } catch (e) {
        toast.error("Impossible de charger les candidats");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, token]);

  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const toggleAll = () => {
    if (selectedIds.size === candidates.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(candidates.map(c => c.id)));
  };

  const handleBulkLink = async () => {
    if (selectedIds.size === 0) {
      toast.error("Sélectionne au moins un contrat");
      return;
    }
    setProcessing(true);
    try {
      const items = candidates
        .filter(c => selectedIds.has(c.id))
        .map(c => ({
          engagement_id: c.id,
          vehicle_data: {
            immatriculation: c.vehicle_info?.immatriculation || null,
            marque: c.vehicle_info?.marque || null,
            modele: c.vehicle_info?.modele || null,
            version: c.vehicle_info?.version || null,
            annee: c.vehicle_info?.annee || null,
            vin: c.vehicle_info?.vin || null,
            kilometrage: c.vehicle_info?.kilometrage || null,
            energie: c.vehicle_info?.energie || null,
          },
          contract_data: {
            type_contrat_auto: c.vehicle_contract_info?.type_contrat_auto || (c.categorie === "assurance" ? "assurance" : "LLD"),
            loueur_ou_assureur: c.vehicle_contract_info?.loueur_ou_assureur || c.prestataire || null,
            duree_mois: c.vehicle_contract_info?.duree_mois || c.duree_engagement_mois || null,
            monthly_rent_ht: c.vehicle_contract_info?.monthly_rent_ht || c.montant_mensuel_ht || null,
            km_annuel_autorise: c.vehicle_contract_info?.km_annuel_autorise || null,
          },
        }));
      const res = await axios.post(`${API}/api/engagements/bulk-link-vehicles`,
        { items }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(res.data);
      toast.success(`${res.data.stats.success}/${res.data.stats.total} liés (${res.data.stats.new_vehicles} nouveaux véhicules)`);
      onSuccess?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur bulk-link");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-600" />
            Import massif Parc Auto
          </DialogTitle>
          <DialogDescription>
            Sélectionne les contrats à lier automatiquement au Parc Auto. Les candidats détectés
            par l&apos;IA (score de confiance élevé) sont pré-cochés.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : results ? (
          <div className="space-y-2">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-900 font-medium">
                <CheckCircle2 className="w-5 h-5" />
                Traitement terminé : {results.stats.success}/{results.stats.total} réussis
              </div>
              <div className="mt-2 text-sm text-emerald-800 grid grid-cols-3 gap-2">
                <div>🆕 Nouveaux véhicules : <strong>{results.stats.new_vehicles}</strong></div>
                <div>♻️ Véhicules réutilisés : <strong>{results.stats.reused_vehicles}</strong></div>
                <div>❌ Erreurs : <strong>{results.stats.errors}</strong></div>
              </div>
            </div>
            {results.results.filter(r => !r.success).map((r, i) => (
              <div key={i} className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                <AlertCircle className="w-3 h-3 inline mr-1" /> {r.engagement_id}: {r.error}
              </div>
            ))}
          </div>
        ) : candidates.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            <Car className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun contrat véhicule à lier détecté.</p>
            <p className="text-xs mt-1">Tous tes contrats de type LLD/LOA/assurance auto sont déjà liés au Parc Auto.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={selectedIds.size === candidates.length && candidates.length > 0}
                onChange={toggleAll}
                data-testid="bulk-toggle-all"
              />
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} / {candidates.length} sélectionnés
              </span>
            </div>
            <div className="border rounded-lg divide-y max-h-[50vh] overflow-y-auto">
              {candidates.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-blue-50 transition ${selectedIds.has(c.id) ? "bg-blue-50/50" : ""}`}
                  data-testid={`bulk-candidate-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{c.prestataire}</span>
                      <Badge variant="outline" className="text-[10px]">{c.categorie}</Badge>
                      {c.auto_detected && (
                        <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-200">🤖 IA</Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">confiance: {c.confidence}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.nom_contrat}</p>
                    <div className="text-xs text-muted-foreground flex gap-3 mt-1 flex-wrap">
                      {c.vehicle_info?.immatriculation && <span>🚗 {c.vehicle_info.immatriculation}</span>}
                      {c.vehicle_info?.marque && <span>{c.vehicle_info.marque} {c.vehicle_info.modele}</span>}
                      {c.montant_mensuel_ht && <span>💰 {c.montant_mensuel_ht}€/mois HT</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          {results ? (
            <Button onClick={() => onOpenChange(false)} data-testid="bulk-close">Fermer</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>Annuler</Button>
              {candidates.length > 0 && (
                <Button
                  onClick={handleBulkLink}
                  disabled={processing || selectedIds.size === 0}
                  className="gap-2"
                  data-testid="bulk-link-confirm"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
                  Lier {selectedIds.size} contrat{selectedIds.size > 1 ? "s" : ""}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
